// ─── NRT product catalogue ───────────────────────────────────────────────────
//
// The products NRT fulfilment stocks — the closed list behind the staff item
// picklist, and the set API-side sku validation is expected to read from.
//
// `isWellFormedNrtSku` only checks a sku's SHAPE, so `NRT-GUM-999MG` passes it.
// Membership of this list is the stronger question: "is this a product we
// ship?". The two are deliberately separate — the shape guard stays the wire
// contract's validator, and this file answers the product question without
// widening or narrowing it.
//
// Clinical product data, not UI copy: `displayName` is the product's English
// name as data (the way a sku is) for staff surfaces, never a locale-catalogue
// key. A member-facing surface must not render it directly — member copy is
// server-authored and localized (ADR-017), so it keys its own label off the sku.

import {
  ORDER_CATALOGUE,
  isEntryOfKind,
  type OrderCatalogueEntryOfKind,
} from '../orders/catalogue';

/** The physical forms NRT fulfilment stocks. */
export const NRT_PRODUCT_FORM = ['gum', 'patch'] as const;
export type NrtProductForm = (typeof NRT_PRODUCT_FORM)[number];

/**
 * Every stocked NRT product, in the order a picklist renders it: gum before
 * patch, gum by ascending dose and patch from the 21 mg starting dose down the
 * step-down. Callers keep this order rather than each re-sorting it.
 *
 * A view over the generic order catalogue (`../orders/catalogue`), not a list of
 * its own: the products are declared once there, and this keeps the NRT shape
 * every existing caller reads.
 */
export const NRT_CATALOGUE = Object.freeze(
  ORDER_CATALOGUE.filter(isEntryOfKind('nrt')).map(({ sku, form, doseMg, displayName }) =>
    Object.freeze({ sku, form, doseMg, displayName }),
  ),
);

export type NrtProduct = (typeof NRT_CATALOGUE)[number];
export type NrtCatalogueSku = OrderCatalogueEntryOfKind<'nrt'>['sku'];

// Widened to `string` by annotation so lookups accept any caller input — a sku
// read off the wire is a string until this answers otherwise.
const PRODUCTS_BY_SKU: ReadonlyMap<string, NrtProduct> = new Map(
  NRT_CATALOGUE.map((product) => [product.sku, product]),
);

/** Is this sku one of the stocked products? Narrows to the catalogue's sku union. */
export function isCatalogueNrtSku(sku: string): sku is NrtCatalogueSku {
  return PRODUCTS_BY_SKU.has(sku);
}

/** The stocked product for a sku, or `undefined` when nothing by that sku is stocked. */
export function findNrtProduct(sku: string): NrtProduct | undefined {
  return PRODUCTS_BY_SKU.get(sku);
}

/** The stocked products of one form, in catalogue order. */
export function nrtCatalogueByForm(form: NrtProductForm): readonly NrtProduct[] {
  return NRT_CATALOGUE.filter((product) => product.form === form);
}
